/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Remove the above eslint-disable when pending game replay is re-enabled
import React, { useCallback, useEffect, useMemo } from "react";
import { Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  TournamentEntry,
  TournamentType,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import "./PendingGameCard.scss";
import HyperCoin from "../../HyperCoin/HyperCoin";
import ImageTimeIcon from "../../../images/time-icon.svg";
import DangerButton from "../DangerButton/DangerButton";
import FilledButton from "../FilledButton/FilledButton";
import useCountdown from "../../../hooks/useCountdown";
import useDisclosure from "../../../hooks/useDisclosure";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../DownloadAppPrompt/DownloadAppPrompt";
import useAuth from "../../../hooks/useAuth";
import {
  GameType,
  ROUTES,
  TOURNAMENT_TO_GAME_TYPE_MAP,
} from "../../../utils/constants";
import ForfeitGameModal from "../../game-play/ForfeitGameModal/ForfeitGameModal";
import { currentPendingTournamentEntryAtom } from "../../../jotai/atoms/tournament";
import useAnalytics from "../../../hooks/useAnalytics";
import { showSuccessToast } from "../../../utils/show-toast";
import useCancelTournamentEntryMutation from "../../../react-query/mutations/use-cancel-tournament-entry";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import DeclineChallengeGameModal from "../../game-play/DeclineChallengeGameModal/DeclineChallengeGameModal";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

export const PENDING_GAME_MARKER = "replay";

function PendingGameCard({
  isScoreProcessed: isScoreProcessedFromBackend,
  refetch,
  ...props
}: TournamentEntry & { refetch: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const analytics = useAnalytics();
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const {
    id: entryId,
    entryExpiresAt,
    participants,
    tournament: {
      id: tournamentId,
      cancelPenalty,
      rewardAmount,
      stakeAmount,
      game: { thumbnail, name: gameName, slug: gameSlug },
      type,
      endDate,
      createdBy,
    },
  } = props;
  const {
    cancelEntry,
    mutation: { isPending: gameIsCancelling, isSuccess: gameIsCancelled },
  } = useCancelTournamentEntryMutation({
    entryId,
  });
  const timeLeft = useCountdown({
    futureDateOrTotalTimeInMs: entryExpiresAt,
    showHours: type === TournamentType.MULTI,
  });
  const setCurrentPendingTournamentEntry = useSetAtom(
    currentPendingTournamentEntryAtom
  );
  const gameType = TOURNAMENT_TO_GAME_TYPE_MAP[type];
  const isBattleGame = gameType === GameType.BATTLE;
  const isUserChallengeOwner = !!userProfile && userProfile?.id !== createdBy;

  const cancelBtnText = useMemo(() => {
    if (type !== TournamentType.CHALLENGE) {
      return t("forfeit");
    }
    if (isUserChallengeOwner) {
      return t("decline");
    }
    return t("cancel");
  }, [isUserChallengeOwner, t, type]);

  const {
    isOpen: isDownloadAppPromptOpen,
    onClose: closeDownloadAppPrompt,
    onOpen: openDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isForfeitModalOpen,
    onOpen: onOpenForfeitModal,
    onClose: onCloseForfeitModal,
  } = useDisclosure();
  const {
    isOpen: isDeclineChallengeModalOpen,
    onOpen: openDeclineChallengeModal,
    onClose: closeDeclineChallengeModal,
  } = useDisclosure();

  const challengeOpponent = useMemo(
    () =>
      participants?.find(
        (participant) => participant.username !== userProfile?.username
      ),
    [participants, userProfile?.username]
  );

  const userTournamentInfo = useMemo(
    () =>
      participants?.find(
        (participant) => participant.username === userProfile?.username
      ),
    [participants, userProfile?.username]
  );

  const doesScoreExist = userTournamentInfo?.score != null;
  const isScoreProcessed = isScoreProcessedFromBackend || doesScoreExist;

  const handleScoreProcessed = useCallback(() => {
    showSuccessToast(t("game_completed_get_app_to_see_result"));
    refetch();
  }, [t, refetch]);

  const handleReplay = useCallback(() => {
    if (isScoreProcessed) {
      handleScoreProcessed();
      return;
    }

    analytics.sendInitiateGameEvent({
      entry_fee: stakeAmount,
      game_name: gameName,
      game_mode: gameType,
    });
    setCurrentPendingTournamentEntry(props);
    const gamePlayRoute = `${ROUTES.GAMES.ROOT}/${gameSlug}/${tournamentId}`;
    navigate(`${gamePlayRoute}?type=${PENDING_GAME_MARKER}`);
  }, [
    analytics,
    gameName,
    gameSlug,
    gameType,
    isScoreProcessed,
    stakeAmount,
    tournamentId,
    props,
    handleScoreProcessed,
    navigate,
    setCurrentPendingTournamentEntry,
  ]);

  const handleForeit = useCallback(() => {
    if (isScoreProcessed) {
      handleScoreProcessed();
      return;
    }

    if (isBattleGame || gameType === GameType.TOURNAMENT) {
      onOpenForfeitModal();
      return;
    }
    if (gameType === GameType.CHALLENGE && !isUserChallengeOwner) {
      cancelEntry();
      return;
    }

    openDeclineChallengeModal();
  }, [
    gameType,
    isBattleGame,
    isScoreProcessed,
    isUserChallengeOwner,
    cancelEntry,
    handleScoreProcessed,
    onOpenForfeitModal,
    openDeclineChallengeModal,
  ]);

  useEffect(() => {
    if (!timeLeft || gameIsCancelled) refetch();
  }, [refetch, timeLeft, gameIsCancelled]);

  return (
    <>
      <div className="pending-game-card">
        <div className="d-flex justify-content-end">
          {type === TournamentType.CHALLENGE && (
            <span className="play-with-text challenge-label">
              {t("play_with")} @{challengeOpponent?.username}
            </span>
          )}
          {isBattleGame && (
            <span className="play-with-text battle-label">
              {t("battle_game")}
            </span>
          )}
          {type === TournamentType.MULTI && (
            <span className="play-with-text tournament-label">
              {t("tournament")}
            </span>
          )}
        </div>
        <div className="d-flex gap-2 content">
          <div>
            <Image
              src={thumbnail}
              alt="game thumbnail"
              height={48}
              width={48}
            />
          </div>
          <div className="w-100 d-flex flex-column justify-content-center">
            <h5>{gameName}</h5>
            <div className="d-flex align-items-center justify-content-between gap-2 reward-and-expiration">
              {type !== TournamentType.MULTI && (
                <>
                  <div className="d-flex">
                    <p className="reward">{t("reward")}</p>
                    <p className="hpr d-flex align-items-center">
                      {!isFiatDisplay && <HyperCoin width={12} height={15} />}
                      <span>
                        {isFiatDisplay
                          ? formatHprToFiatValue(rewardAmount)
                          : rewardAmount}
                      </span>
                    </p>
                  </div>
                  <span className="dot" />
                </>
              )}
              <div className="replay-before-text">
                <Image src={ImageTimeIcon} alt="clock" /> {t("replay_before")}{" "}
                <span>{timeLeft}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-between gap-3 btn-box">
          <DangerButton
            className="cancel-btn"
            onClick={handleForeit}
            disabled={gameIsCancelling}
          >
            {cancelBtnText}{" "}
            {((type === TournamentType.CHALLENGE && isUserChallengeOwner) ||
              isBattleGame) && (
              <>
                {!isFiatDisplay && <HyperCoin className="ms-1" />} -
                {isFiatDisplay
                  ? formatHprToFiatValue(cancelPenalty || stakeAmount)
                  : cancelPenalty || stakeAmount}
              </>
            )}
            {gameIsCancelling && <CustomSpinner size="sm" className="ms-1" />}
          </DangerButton>
          <FilledButton
            onClick={handleReplay}
            disabled={gameIsCancelling}
            className="replay-for-free-btn"
          >
            {t("replay_for_free")}
          </FilledButton>
        </div>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={
          PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_PENDING_GAMES
        }
      />
      <ForfeitGameModal
        entryId={entryId}
        onHide={onCloseForfeitModal}
        show={isForfeitModalOpen}
        thumbnail={thumbnail}
        stakeAmount={stakeAmount}
        refetch={refetch}
        gameName={gameName}
        gameType={gameType}
      />
      <DeclineChallengeGameModal
        isOpen={isDeclineChallengeModalOpen}
        onClose={closeDeclineChallengeModal}
        gameType={gameType}
        refetch={refetch}
        {...props}
      />
    </>
  );
}

export default PendingGameCard;
